import { useState, useCallback, useRef, useEffect } from "react";

type AsyncFn<Args extends any[] = any[], Return = any> = (
    ...args: Args
) => Promise<Return>;

export function useGenericAsyncHandler<Args extends any[], Return>(
    asyncFn: AsyncFn<Args, Return>,
) {
    const [loading, setLoading] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const execute = useCallback(
        async (...args: Args): Promise<Return> => {
            setLoading(true);
            try {
                const result = await asyncFn(...args);
                return result;
            } finally {
                // Only update state if component is still mounted
                if (isMountedRef.current) {
                    setLoading(false);
                }
            }
        },
        [asyncFn],
    );

    return { execute, loading };
}
